import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { getUserIdFromToken, getUserIdFromStorage } from "./utils/auth";
import './assets/main.css';
import styles from './HomeDashboard.module.css';

/* ---------------- UI Primitives ---------------- */

const Card = ({ children, className = "" }) => (
  <div className={`${styles.card} ${className}`}>
    {children}
  </div>
);

const Button = ({ children, onClick, variant = "primary", className = "" }) => {
  const variantClass = variant === "primary" ? styles.buttonPrimary : styles.buttonSecondary;

  return (
    <button
      onClick={onClick}
      className={`${styles.button} ${variantClass} ${className}`}
    >
      {children}
    </button>
  );
};

const StatCard = ({ label, value, subtitle, color = "gray" }) => {
  const colorClass = styles[`value${color.charAt(0).toUpperCase() + color.slice(1)}`];

  return (
    <Card className={styles.statCard}>
      <p className={styles.label}>{label}</p>
      <p className={`${styles.value} ${colorClass}`}>
        {value}
      </p>
      {subtitle && (
        <p className={styles.subtitle}>{subtitle}</p>
      )}
    </Card>
  );
};

/* ---------------- Page ---------------- */

export default function DashboardHomePage() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [sets, setSets] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedSet, setSelectedSet] = useState("");
  const [timeRange, setTimeRange] = useState("all"); // "day", "week", "month", "year", "all"
  const [graphMetric, setGraphMetric] = useState("overall"); // "overall" or rarity name
  const statsRef = useRef(null);

  useEffect(() => {
    let userId = getUserIdFromToken();
    
    if (!userId) {
      console.log('Could not get userId from token, trying storage...');
      userId = getUserIdFromStorage();
    }
    
    console.log('Dashboard userId:', userId);
    
    if (userId) {
      fetch(`${process.env.REACT_APP_API_URL}/api/sessions?userId=${userId}`)
        .then(r => r.json())
        .then(data => {
          console.log('Fetched sessions:', data);
          setSessions(data);
        });
      
      fetch(`${process.env.REACT_APP_API_URL}/api/groups?userId=${userId}`)
        .then(r => r.json())
        .then(setGroups);
    } else {
      console.warn('No userId found, fetching all sessions');
      fetch(`${process.env.REACT_APP_API_URL}/api/sessions`)
        .then(r => r.json())
        .then(setSessions);
      
      fetch(`${process.env.REACT_APP_API_URL}/api/groups`)
        .then(r => r.json())
        .then(setGroups);
    }
    
    fetch(`${process.env.REACT_APP_API_URL}/api/sets`).then(r => r.json()).then(setSets);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    navigate('/');
  };

  const handleDeleteGroup = async () => {
    if (!selectedGroup) return;
    
    const groupToDelete = groups.find(g => g._id === selectedGroup);
    if (!groupToDelete) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to delete the group "${groupToDelete.name}"? This will permanently delete the group and ALL sessions within it. This action cannot be undone.`
    );
    
    if (!confirmed) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/groups/${selectedGroup}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        alert(`Failed to delete group: ${errorData.error || 'Unknown error'}`);
        return;
      }
      
      // Remove from local state and clear selection
      setGroups(groups.filter(g => g._id !== selectedGroup));
      setSelectedGroup("");
      
      // Reload sessions to reflect the deletions
      let userId = getUserIdFromToken();
      if (!userId) {
        userId = getUserIdFromStorage();
      }
      
      if (userId) {
        const sessionsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/sessions?userId=${userId}`);
        const sessionsData = await sessionsResponse.json();
        setSessions(sessionsData);
      }
      
      alert('Group and all associated sessions deleted successfully!');
    } catch (error) {
      console.error('Error deleting group:', error);
      alert('Failed to delete group');
    }
  };

  const handleExportStats = async () => {
    if (!statsRef.current) return;
    
    try {
      // Dynamically import html2canvas
      const html2canvas = (await import('html2canvas')).default;
      
      const canvas = await html2canvas(statsRef.current, {
        backgroundColor: '#f9fafb',
        scale: 2,
        logging: false,
        useCORS: true
      });
      
      // Convert to blob
      canvas.toBlob(async (blob) => {
        const setName = selectedSet ? sets.find(s => s._id === selectedSet)?.name : 'All Sets';
        const fileName = `${setName}-stats-${new Date().toLocaleDateString().replace(/\//g, '-')}.png`;
        
        // Check if Web Share API is supported (mobile devices)
        if (navigator.share && navigator.canShare) {
          try {
            const file = new File([blob], fileName, { type: 'image/png' });
            
            // Check if we can share files
            if (navigator.canShare({ files: [file] })) {
              await navigator.share({
                files: [file],
                title: `${setName} Statistics`,
                text: `Check out my Pokémon pull statistics for ${setName}!`
              });
              return;
            }
          } catch (shareError) {
            console.log('Share failed or was cancelled:', shareError);
            // Fall through to download if share fails/cancelled
          }
        }
        
        // Fallback to download for desktop or if share fails
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = fileName;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
        alert('Stats exported successfully!');
      });
    } catch (error) {
      console.error('Error exporting stats:', error);
      alert('Failed to export stats. Please try again.');
    }
  };

  const filteredSessions = useMemo(() => {
    const now = new Date();
    const cutoffDate = new Date();
    
    // Calculate cutoff date based on time range
    switch (timeRange) {
      case "day":
        cutoffDate.setDate(now.getDate() - 1);
        break;
      case "week":
        cutoffDate.setDate(now.getDate() - 7);
        break;
      case "month":
        cutoffDate.setMonth(now.getMonth() - 1);
        break;
      case "year":
        cutoffDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        cutoffDate.setFullYear(2000); // Show all
    }
    
    return sessions.filter(s => {
      if (selectedGroup && s.group?._id !== selectedGroup) return false;
      if (selectedSet && s.set?._id !== selectedSet) return false;
      
      // Filter by time range if not "all"
      if (timeRange !== "all") {
        const sessionDate = new Date(s.datePulled || s.createdAt);
        if (sessionDate < cutoffDate) return false;
      }
      
      return true;
    });
  }, [sessions, selectedGroup, selectedSet, timeRange]);

  const stats = useMemo(() => {
    let totalPacks = 0;
    const rarityTotals = {};
    const rarityExpectedRates = {};

    // Get the selected set object if a specific set is selected
    const selectedSetObj = selectedSet ? sets.find(s => s._id === selectedSet) : null;

    for (const s of filteredSessions) {
      totalPacks += s.packsOpened;

      console.log('Processing session for stats:', s);

      
      // Get expected hit rates from the selected set (or from session's set if viewing all)
      const setToUse = selectedSetObj || s.set;
      if (setToUse && setToUse.rarities) {
        setToUse.rarities.forEach(rarity => {
          if (!rarityExpectedRates[rarity.name]) {
            rarityExpectedRates[rarity.name] = rarity.hitRate;
          }
        });
      }

      for (const r of s.cardsByRarity) {
        rarityTotals[r.rarity] =
          (rarityTotals[r.rarity] || 0) + r.count;
      }
    }

    // Calculate pull rates and luck
    const rarityStats = {};
    let overallLuckScore = 0;
    let raritiesWithRates = 0;

    Object.entries(rarityTotals).forEach(([rarity, count]) => {
      const actualRate = totalPacks > 0 ? (count / totalPacks) : 0;
      const expectedRate = rarityExpectedRates[rarity];
      
      rarityStats[rarity] = {
        count,
        actualRate,
        expectedRate,
        actualPer: expectedRate && expectedRate > 0 ? (1 / actualRate) : 0,
        expectedPer: expectedRate || 0
      };

      // Calculate luck (how much better/worse than expected)
      if (expectedRate && expectedRate > 0) {
        const expectedCount = totalPacks / expectedRate;
        const luckMultiplier = count / expectedCount;
        rarityStats[rarity].luck = luckMultiplier;
        overallLuckScore += luckMultiplier;
        raritiesWithRates++;
      }
    });

    const averageLuck = raritiesWithRates > 0 ? overallLuckScore / raritiesWithRates : 1;

    console.log('Dashboard stats computed:', {rarityTotals, rarityStats, averageLuck});
    return { 
      totalPacks, 
      rarityTotals, 
      rarityStats,
      averageLuck
    };
  }, [filteredSessions, selectedSet, sets]);

  // Calculate luck per set (for "All Sets" view)
  const setLuckStats = useMemo(() => {
    if (selectedSet) return []; // Only calculate for "All Sets" view
    
    const setStats = {};
    
    // Group sessions by set
    filteredSessions.forEach(session => {
      if (!session.set || !session.set._id) return;
      
      const setId = session.set._id;
      
      // Find the full set object from the sets array to get rarities
      const fullSetObj = sets.find(s => s._id === setId);
      
      if (!setStats[setId]) {
        setStats[setId] = {
          setId: setId,
          setName: session.set.name || 'Unknown Set',
          setCode: session.set.code,
          totalPacks: 0,
          rarityTotals: {},
          setRarities: fullSetObj?.rarities || []
        };
      }
      
      setStats[setId].totalPacks += session.packsOpened;
      
      session.cardsByRarity.forEach(r => {
        setStats[setId].rarityTotals[r.rarity] = 
          (setStats[setId].rarityTotals[r.rarity] || 0) + r.count;
      });
    });
    
    // Calculate luck for each set
    const setLuckArray = Object.values(setStats).map(setData => {
      let luckScore = 0;
      let raritiesWithRates = 0;
      
      setData.setRarities.forEach(rarity => {
        const actualCount = setData.rarityTotals[rarity.name] || 0;
        const expectedRate = rarity.hitRate;
        
        if (expectedRate && expectedRate > 0 && setData.totalPacks > 0) {
          const expectedCount = setData.totalPacks / expectedRate;
          if (expectedCount > 0) {
            const luckMultiplier = actualCount / expectedCount;
            luckScore += luckMultiplier;
            raritiesWithRates++;
          }
        }
      });
      
      const averageLuck = raritiesWithRates > 0 ? luckScore / raritiesWithRates : 0;
      
      return {
        ...setData,
        averageLuck,
        hasData: raritiesWithRates > 0
      };
    });
    
    // Filter out sets with no data and sort by luck
    return setLuckArray
      .filter(s => s.hasData && s.totalPacks > 0)
      .sort((a, b) => b.averageLuck - a.averageLuck);
  }, [filteredSessions, selectedSet, sets]);

  // Calculate luck/pull rate over time for the graph
  const graphData = useMemo(() => {
    if (!selectedSet) return [];
    
    const selectedSetObj = sets.find(s => s._id === selectedSet);
    if (!selectedSetObj || !selectedSetObj.rarities) return [];
    
    // Sort sessions by date
    const sortedSessions = [...filteredSessions].sort((a, b) => {
      const dateA = new Date(a.datePulled || a.createdAt);
      const dateB = new Date(b.datePulled || b.createdAt);
      return dateA - dateB;
    });
    
    const dataPoints = [];
    let cumulativePacks = 0;
    const cumulativeRarityTotals = {};
    
    sortedSessions.forEach((session, index) => {
      cumulativePacks += session.packsOpened;
      
      // Add this session's cards to cumulative totals
      session.cardsByRarity.forEach(r => {
        cumulativeRarityTotals[r.rarity] = (cumulativeRarityTotals[r.rarity] || 0) + r.count;
      });
      
      let value;
      
      if (graphMetric === "overall") {
        // Calculate overall luck at this point in time
        let luckScore = 0;
        let raritiesWithRates = 0;
        
        selectedSetObj.rarities.forEach(rarity => {
          const actualCount = cumulativeRarityTotals[rarity.name] || 0;
          const expectedRate = rarity.hitRate;
          
          if (expectedRate && expectedRate > 0 && cumulativePacks > 0) {
            const expectedCount = cumulativePacks / expectedRate;
            if (expectedCount > 0) {
              const luckMultiplier = actualCount / expectedCount;
              luckScore += luckMultiplier;
              raritiesWithRates++;
            }
          }
        });
        
        value = raritiesWithRates > 0 ? luckScore / raritiesWithRates : 1;
      } else {
        // Calculate pull rate for specific rarity
        const rarityCount = cumulativeRarityTotals[graphMetric] || 0;
        const pullRate = cumulativePacks > 0 ? (rarityCount / cumulativePacks) * 100 : 0;
        value = pullRate;
      }
      
      dataPoints.push({
        date: new Date(session.datePulled || session.createdAt),
        value: value,
        sessionIndex: index + 1,
        packs: cumulativePacks,
        rarityCount: graphMetric !== "overall" ? (cumulativeRarityTotals[graphMetric] || 0) : undefined
      });
    });
    
    return dataPoints;
  }, [filteredSessions, selectedSet, sets, graphMetric]);

  return (
    <div className={styles.pageContainer}>
      <div className={styles.contentWrapper}>

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h1>Dashboard</h1>
            <p>Overview of your pull history</p>
          </div>

          <div className={styles.headerActions}>
            <Button onClick={() => navigate("/sessions")} variant="secondary">
              View History
            </Button>
            <Button onClick={() => navigate("/sets")} variant="secondary">
                  Manage Sets
                </Button>
            {process.env.REACT_APP_SET_CREATION === 'enabled' && (
              <>
                
                <Button onClick={() => navigate("/sets/new")} variant="secondary">
                  Add Set
                </Button>
              </>
            )}
            <Button onClick={() => navigate("/sessions/new")}>
              New Session
            </Button>
            <Button 
              onClick={() => window.open('https://ko-fi.com/potapie', '_blank')}
              variant="secondary"
              style={{ backgroundColor: '#FF5E5B', color: 'white', borderColor: '#FF5E5B' }}
            >
              ☕ Donate
            </Button>
            <Button onClick={handleLogout} variant="secondary">
              Logout
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className={styles.filtersCard}>
          <div className={styles.filtersGrid}>
            <select
              className={styles.filterSelect}
              value={selectedGroup}
              onChange={e => setSelectedGroup(e.target.value)}
            >
              <option value="">All Groups</option>
              {groups.map(g => (
                <option key={g._id} value={g._id}>{g.name}</option>
              ))}
            </select>

            <select
              className={styles.filterSelect}
              value={selectedSet}
              onChange={e => setSelectedSet(e.target.value)}
            >
              <option value="">All Sets</option>
              {sets.map(s => (
                <option key={s._id} value={s._id}>{s.name}</option>
              ))}
            </select>

            <select
              className={styles.filterSelect}
              value={timeRange}
              onChange={e => setTimeRange(e.target.value)}
            >
              <option value="all">All Time</option>
              <option value="day">Past Day</option>
              <option value="week">Past Week</option>
              <option value="month">Past Month</option>
              <option value="year">Past Year</option>
            </select>

            <Button
              variant="secondary"
              onClick={() => {
                setSelectedGroup("");
                setSelectedSet("");
                setTimeRange("all");
              }}
            >
              Clear Filters
            </Button>
          </div>
        </Card>

        {/* Stats */}
        {!selectedSet ? (
          // All Sets View - Simple Overview
          <>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Overview</h2>
              <div className={styles.sectionActions}>
                {selectedGroup && (
                  <Button
                    variant="secondary"
                    onClick={handleDeleteGroup}
                    className={styles.deleteGroupButton}
                  >
                    🗑️ Delete Group
                  </Button>
                )}
                <Button
                  variant="secondary"
                  onClick={handleExportStats}
                >
                  📤 Export Stats
                </Button>
              </div>
            </div>

            <div ref={statsRef} className={styles.exportableContent}>
              <div className={styles.statsGrid}>
                <StatCard label="Sessions" value={filteredSessions.length} color="gray" />
                <StatCard label="Packs Opened" value={stats.totalPacks} color="blue" />
                <StatCard 
                  label="Total Pulls" 
                  value={Object.values(stats.rarityTotals).reduce((a, b) => a + b, 0)}
                  color="purple"
                />
                <StatCard 
                  label="Unique Rarities" 
                  value={Object.keys(stats.rarityTotals).length}
                  color="gray"
                />
              </div>

              {/* Best and Worst Sets by Luck */}
              {setLuckStats.length > 0 && (
                <div className={styles.setLuckSection}>
                  <div className={styles.setLuckGrid}>
                    {/* Best Sets */}
                    <Card>
                      <div className={styles.cardHeader}>
                        <h2 className={styles.cardTitle}>🎉 Luckiest Sets</h2>
                        <p className={styles.cardSubtitle}>Overall Luck</p>
                      </div>
                      <div className={styles.setLuckList}>
                        {setLuckStats.slice(0, 3).map((setData, index) => (
                          <div key={setData.setId} className={styles.setLuckItem}>
                            <div className={styles.setLuckRank}>#{index + 1}</div>
                            <div className={styles.setLuckDetails}>
                              <div className={styles.setLuckName}>{setData.setName}</div>
                              <div className={styles.setLuckMeta}>
                                {setData.totalPacks} packs opened
                              </div>
                            </div>
                            <div className={styles.setLuckValue}>
                              <span className={styles.setLuckPercentGreen}>
                                {(setData.averageLuck * 100).toFixed(0)}%
                              </span>
                            </div>
                          </div>
                        ))}
                        {setLuckStats.length === 0 && (
                          <p className={styles.emptyState}>
                            No data available yet.
                          </p>
                        )}
                      </div>
                    </Card>

                    {/* Worst Sets */}
                    <Card>
                      <div className={styles.cardHeader}>
                        <h2 className={styles.cardTitle}>📉 Unluckiest Sets</h2>
                        <p className={styles.cardSubtitle}>Overall Luck</p>
                      </div>
                      <div className={styles.setLuckList}>
                        {setLuckStats.slice(-3).reverse().map((setData, index) => {
                          const actualIndex = index + 1;
                          return (
                            <div key={setData.setId} className={styles.setLuckItem}>
                              <div className={styles.setLuckRank}>#{actualIndex}</div>
                              <div className={styles.setLuckDetails}>
                                <div className={styles.setLuckName}>{setData.setName}</div>
                                <div className={styles.setLuckMeta}>
                                  {setData.totalPacks} packs opened
                                </div>
                              </div>
                              <div className={styles.setLuckValue}>
                                <span className={styles.setLuckPercentRed}>
                                  {(setData.averageLuck * 100).toFixed(0)}%
                                </span>
                              </div>
                            </div>
                          );
                        })}
                        {setLuckStats.length === 0 && (
                          <p className={styles.emptyState}>
                            No data available yet.
                          </p>
                        )}
                      </div>
                    </Card>
                  </div>
                </div>
              )}

              {/* Rarity Breakdown */}
              <Card>
                <div className={styles.cardHeader}>
                  <h2 className={styles.cardTitle}>
                    Rarity Breakdown (All Sets)
                  </h2>
                </div>

                <div className={styles.rarityList}>
                  {Object.keys(stats.rarityTotals).length === 0 && (
                    <p className={styles.emptyState}>
                      No data available for selected filters.
                    </p>
                  )}

                  {Object.entries(stats.rarityTotals)
                    .sort(([, a], [, b]) => b - a)
                    .map(([rarity, count]) => {
                      const max = Math.max(...Object.values(stats.rarityTotals));
                      const pct = (count / max) * 100;

                      return (
                        <div key={rarity} className={styles.rarityItem}>
                          <div className={styles.rarityItemHeader}>
                            <span className={styles.rarityName}>{rarity}</span>
                            <span className={styles.rarityCount}>{count}</span>
                          </div>
                          <div className={styles.progressBar}>
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${pct}%` }}
                              transition={{ duration: 0.6 }}
                              className={styles.progressBarFill}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </Card>
            </div>
          </>
        ) : (
          // Specific Set View - Detailed Statistics with Luck
          <>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Overview</h2>
              <Button
                variant="secondary"
                onClick={handleExportStats}
              >
                📤 Export Stats
              </Button>
            </div>

            <div ref={statsRef} className={styles.exportableContent}>
              <div className={styles.statsGrid}>
                <StatCard label="Sessions" value={filteredSessions.length} color="gray" />
                <StatCard label="Packs Opened" value={stats.totalPacks} color="blue" />
                <StatCard 
                  label="Overall Luck" 
                  value={`${(stats.averageLuck * 100).toFixed(1)}%`}
                  subtitle={stats.averageLuck >= 1 ? "Above average! 🎉" : "Below average"}
                  color={stats.averageLuck >= 1.1 ? "green" : stats.averageLuck >= 0.9 ? "gray" : "red"}
                />
                <StatCard 
                  label="Total Pulls" 
                  value={Object.values(stats.rarityTotals).reduce((a, b) => a + b, 0)}
                  color="purple"
                />
              </div>

              {/* Rarity Statistics */}
              <div>
                <h2 className={styles.sectionTitle}>Pull Rates by Rarity</h2>
                <div className={styles.rarityStatsGrid}>
                  {Object.entries(stats.rarityStats)
                    .sort(([, a], [, b]) => b.count - a.count)
                    .map(([rarity, data]) => {
                      const luckColorClass = data.luck >= 1.1 
                        ? styles.rarityStatLuckGreen 
                        : data.luck >= 0.9 
                        ? styles.rarityStatLuckBlue 
                        : styles.rarityStatLuckRed;
                      const luckText = data.luck 
                        ? `${(data.luck * 100).toFixed(0)}% of expected`
                        : "No baseline data";

                      return (
                        <Card key={rarity} className={styles.rarityStatCard}>
                          <div className={styles.rarityStatHeader}>
                            <h3 className={styles.rarityStatTitle}>{rarity}</h3>
                            <span className={styles.rarityStatBadge}>
                              {data.count}
                            </span>
                          </div>
                          
                          <div className={styles.rarityStatDetails}>
                            <div className={styles.rarityStatRow}>
                              <span className={styles.rarityStatLabel}>Your Rate:</span>
                              <span className={styles.rarityStatValue}>
                                {data.actualPer > 0 ? `1/${data.actualPer.toFixed(1)}` : 'N/A'}
                              </span>
                            </div>
                            
                            {data.expectedPer > 0 && (
                              <div className={styles.rarityStatRow}>
                                <span className={styles.rarityStatLabel}>Expected:</span>
                                <span className={styles.rarityStatValueMedium}>
                                  1/{data.expectedPer}
                                </span>
                              </div>
                            )}
                            
                            <div className={styles.rarityStatRow}>
                              <span className={styles.rarityStatLabel}>Pull Rate:</span>
                              <span className={styles.rarityStatValue}>
                                {(data.actualRate * 100).toFixed(2)}%
                              </span>
                            </div>

                            {data.luck && (
                              <div className={styles.rarityStatLuck}>
                                <div className={styles.rarityStatLuckRow}>
                                  <span className={styles.rarityStatLuckLabel}>Luck:</span>
                                  <span className={`${styles.rarityStatLuckValue} ${luckColorClass}`}>
                                    {luckText}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        </Card>
                      );
                    })}
                </div>
                
                {Object.keys(stats.rarityStats).length === 0 && (
                  <Card className={styles.rarityBreakdownCard}>
                    <p className={styles.emptyState}>
                      No rarity data available. Start tracking sessions to see statistics!
                    </p>
                  </Card>
                )}
              </div>

              {/* Graph Over Time */}
              {graphData.length > 0 && (
                <Card className={styles.graphCard}>
                  <div className={styles.graphCardHeader}>
                    <div>
                      <h2 className={styles.graphTitle}>
                        {graphMetric === "overall" ? "Luck Over Time" : `${graphMetric} Pull Rate Over Time`}
                      </h2>
                      <p className={styles.graphSubtitle}>
                        {graphMetric === "overall" 
                          ? "Track how your luck changes as you open more packs"
                          : `Track your pull rate for ${graphMetric} over time`
                        }
                      </p>
                    </div>
                    <select
                      className={styles.graphMetricSelect}
                      value={graphMetric}
                      onChange={e => setGraphMetric(e.target.value)}
                    >
                      <option value="overall">Overall Luck</option>
                      {sets.find(s => s._id === selectedSet)?.rarities.map(rarity => (
                        <option key={rarity.name} value={rarity.name}>{rarity.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className={styles.graphContent}>
                    <div className={styles.graphContainer}>
                      {/* Y-axis labels */}
                      <div className={styles.yAxisLabels}>
                        {graphMetric === "overall" ? (
                          <>
                            <span>200%</span>
                            <span>150%</span>
                            <span>100%</span>
                            <span>50%</span>
                            <span>0%</span>
                          </>
                        ) : (
                          <>
                            <span>20%</span>
                            <span>15%</span>
                            <span>10%</span>
                            <span>5%</span>
                            <span>0%</span>
                          </>
                        )}
                      </div>
                      
                      {/* Graph area */}
                      <div className={styles.graphArea}>
                        {/* Expected line (only for overall luck) */}
                        {graphMetric === "overall" && (
                          <div className={styles.expectedLine}>
                            <span className={styles.expectedLabel}>Expected</span>
                          </div>
                        )}
                        
                        {/* Line graph */}
                        <svg className={styles.graphSvg} viewBox="0 0 1000 320">
                          <defs>
                            <linearGradient id="graphGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.2"/>
                              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0"/>
                            </linearGradient>
                          </defs>
                          
                          {/* Area under the line */}
                          {graphData.length > 1 && (
                            <polygon
                              points={
                                graphData.map((point, index) => {
                                  const x = (index / (graphData.length - 1)) * 1000;
                                  const maxValue = graphMetric === "overall" ? 200 : 20;
                                  const y = 320 - (Math.min(point.value * (graphMetric === "overall" ? 100 : 1), maxValue) / maxValue * 320);
                                  return `${x},${y}`;
                                }).join(' ') + ` 1000,320 0,320`
                              }
                              fill="url(#graphGradient)"
                            />
                          )}
                          
                          {/* Line */}
                          <polyline
                            points={graphData.map((point, index) => {
                              const x = (index / (graphData.length - 1 || 1)) * 1000;
                              const maxValue = graphMetric === "overall" ? 200 : 20;
                              const y = 320 - (Math.min(point.value * (graphMetric === "overall" ? 100 : 1), maxValue) / maxValue * 320);
                              return `${x},${y}`;
                            }).join(' ')}
                            fill="none"
                            stroke="#3b82f6"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          
                          {/* Data points */}
                          {graphData.map((point, index) => {
                            const x = (index / (graphData.length - 1 || 1)) * 1000;
                            const maxValue = graphMetric === "overall" ? 200 : 20;
                            const y = 320 - (Math.min(point.value * (graphMetric === "overall" ? 100 : 1), maxValue) / maxValue * 320);
                            
                            const tooltipText = graphMetric === "overall"
                              ? `Session ${point.sessionIndex}: ${(point.value * 100).toFixed(1)}% luck\nTotal packs: ${point.packs}\nDate: ${point.date.toLocaleDateString()}`
                              : `Session ${point.sessionIndex}: ${point.value.toFixed(2)}% pull rate\nTotal ${graphMetric}: ${point.rarityCount}\nTotal packs: ${point.packs}\nDate: ${point.date.toLocaleDateString()}`;
                            
                            return (
                              <g key={index}>
                                <circle
                                  cx={x}
                                  cy={y}
                                  r="6"
                                  fill="white"
                                  stroke="#3b82f6"
                                  strokeWidth="2"
                                  className={styles.dataPointCircle}
                                >
                                  <title>{tooltipText}</title>
                                </circle>
                                <circle
                                  cx={x}
                                  cy={y}
                                  r="15"
                                  fill="transparent"
                                  className={styles.dataPointHitArea}
                                >
                                  <title>{tooltipText}</title>
                                </circle>
                              </g>
                            );
                          })}
                        </svg>
                      </div>
                    </div>
                    
                    {/* X-axis label */}
                    <div className={styles.xAxisLabel}>
                      Sessions Over Time
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}